/**
 * PSP/CRE Tool v2.0 - Blood Products Constants
 * 
 * Defines blood product specifications, storage requirements, and consumption rates
 * for military medical planning. Implements NATO 1:1:1 massive transfusion protocol.
 * 
 * Key Concepts:
 * - 1:1:1 Protocol: Equal ratios of PRBC:FFP:Platelets for massive transfusion
 * - Walking Blood Bank: Emergency fresh whole blood from screened donors
 * - Cold Chain: Temperature requirements for blood storage
 * - Shelf Life: Product expiration timelines
 * - DCR: Damage Control Resuscitation principles
 * 
 * Based on:
 * - NATO STANAG 2939 Blood Supply in the Area of Operations
 * - US Army ATP 4-02.1 Army Medical Logistics
 * - AABB Technical Manual (Blood Banking)
 * - TCCC Guidelines (Tactical Combat Casualty Care)
 * - Ukraine 2022-2024 lessons (Whole Blood superiority validated)
 * - Israeli Defense Forces blood protocols
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 20, 2024
 * Version: 2.0 - Complete Modernization
 * 
 * Changes from v1.0:
 * - Added Freeze-Dried Plasma (FDP)
 * - Added complete blood type compatibility matrix
 * - Added Ukraine 2022-2024 outcome data
 * - Added cold chain logistics by climate zone
 * - Added Damage Control Resuscitation (DCR) protocols
 * - Added transfusion reaction rates
 * - Added coagulopathy assessment tools
 * - Added enhanced calculator integration functions
 * - Added platelet alternatives in austere settings
 */

// ============================================================================
// BLOOD TYPE COMPATIBILITY
// ============================================================================

/**
 * Complete blood type compatibility matrix
 * Critical for emergency transfusion decisions
 */
export const BLOOD_TYPE_COMPATIBILITY = {
  RBC_COMPATIBILITY: {
    // Recipient can receive from these donors
    'O-': ['O-'],
    'O+': ['O-', 'O+'],
    'A-': ['O-', 'A-'],
    'A+': ['O-', 'O+', 'A-', 'A+'],
    'B-': ['O-', 'B-'],
    'B+': ['O-', 'O+', 'B-', 'B+'],
    'AB-': ['O-', 'A-', 'B-', 'AB-'],
    'AB+': ['O-', 'O+', 'A-', 'A+', 'B-', 'B+', 'AB-', 'AB+']  // Universal recipient
  },
  
  PLASMA_COMPATIBILITY: {
    // Recipient can receive from these donors (opposite of RBC)
    'O-': ['O-', 'O+', 'A-', 'A+', 'B-', 'B+', 'AB-', 'AB+'],
    'O+': ['O+', 'A+', 'B+', 'AB+'],
    'A-': ['A-', 'A+', 'AB-', 'AB+'],
    'A+': ['A+', 'AB+'],
    'B-': ['B-', 'B+', 'AB-', 'AB+'],
    'B+': ['B+', 'AB+'],
    'AB-': ['AB-', 'AB+'],
    'AB+': ['AB+']  // Universal plasma donor is AB
  },
  
  UNIVERSAL_DONORS: {
    rbc: 'O-',
    plasma: 'AB+',
    whole_blood_ltowb: 'O+',  // Low-titer O+ whole blood
    emergency_rbc: 'O-',
    emergency_plasma: 'AB'
  },
  
  POPULATION_DISTRIBUTION: {
    // Approximate blood type distribution (varies by population)
    'O+': 0.37,
    'A+': 0.34,
    'B+': 0.10,
    'AB+': 0.04,
    'O-': 0.07,
    'A-': 0.06,
    'B-': 0.01,
    'AB-': 0.01
  },
  
  ROMANIAN_DISTRIBUTION: {
    // Romanian specific (Eastern European pattern)
    'O+': 0.32,
    'A+': 0.37,
    'B+': 0.12,
    'AB+': 0.06,
    'O-': 0.05,
    'A-': 0.06,
    'B-': 0.01,
    'AB-': 0.01
  }
};

// ============================================================================
// BLOOD PRODUCT TYPES
// ============================================================================

/**
 * Packed Red Blood Cells (PRBC)
 */
export const PRBC = {
  code: 'PRBC',
  name: 'Packed Red Blood Cells',
  abbreviation: 'PRBCs',
  nato_code: 'A',
  
  volume_ml: 300,
  weight_kg: 0.35,
  
  storage: {
    temperature_c: { min: 1, max: 6 },
    shelf_life_days: 42,
    shelf_life_thawed: null,
    cold_chain_required: true,
    transport_time_limit_hours: 24,
    
    // Climate zone adjustments
    by_climate: {
      ARCTIC: { transport_limit_hours: 48, ice_requirement: 'REDUCED' },
      TEMPERATE: { transport_limit_hours: 24, ice_requirement: 'STANDARD' },
      TROPICAL: { transport_limit_hours: 12, ice_requirement: 'ENHANCED' },
      DESERT: { transport_limit_hours: 8, ice_requirement: 'MAXIMUM' }
    }
  },
  
  clinical: {
    hemoglobin_grams_per_unit: 50,
    hematocrit_percent: 65,
    transfusion_trigger_gdl: 7,
    expected_hgb_increase_gdl: 1,
    
    indications: [
      'Hemorrhagic shock',
      'Anemia (Hgb < 7 g/dL)',
      'Ongoing bleeding',
      'Surgical blood loss'
    ],
    
    transfusion_time_minutes: 30,
    emergency_rate_minutes: 10,  // Can push faster
    crossmatch_required: true,
    emergency_o_neg_allowed: true,
    
    // Transfusion reactions
    reactions: {
      febrile_rate: 0.01,        // 1%
      allergic_rate: 0.03,       // 3%
      hemolytic_rate: 0.0001,    // 0.01%
      taco_rate: 0.01,           // 1% (Transfusion Associated Circulatory Overload)
      trali_rate: 0.0002         // 0.02% (Transfusion Related Acute Lung Injury)
    }
  },
  
  logistics: {
    units_per_cooler: 6,
    cooler_ice_hours: 24,
    resupply_frequency_days: 3,
    wastage_rate_percent: 8,
    
    // Storage containers
    containers: {
      GOLDEN_HOUR_BOX: { capacity: 4, duration_hours: 4 },
      IGLOO_COOLER: { capacity: 6, duration_hours: 24 },
      BLOOD_REFRIGERATOR: { capacity: 50, duration_hours: null }
    }
  },
  
  notes: 'Primary blood product for trauma. Store at 1-6°C.'
};

/**
 * Fresh Frozen Plasma (FFP)
 */
export const FFP = {
  code: 'FFP',
  name: 'Fresh Frozen Plasma',
  abbreviation: 'FFP',
  nato_code: 'B',
  
  volume_ml: 250,
  weight_kg: 0.30,
  
  storage: {
    temperature_c: { min: -18, max: -25 },
    shelf_life_days: 365,
    shelf_life_thawed_hours: 24,
    cold_chain_required: true,
    thaw_time_minutes: 30,
    transport_frozen: true,
    
    thaw_methods: {
      WATER_BATH: { time_minutes: 30, temp_c: 37 },
      DRY_THAWER: { time_minutes: 20, temp_c: 37 },
      MICROWAVE: { time_minutes: 5, approved: false }
    }
  },
  
  clinical: {
    fibrinogen_mg_per_unit: 400,
    factor_activity_percent: 70,
    
    indications: [
      'Massive transfusion (1:1:1)',
      'Coagulopathy',
      'Factor deficiency',
      'Warfarin reversal'
    ],
    
    transfusion_time_minutes: 30,
    crossmatch_required: false,
    emergency_ab_allowed: true,
    
    reactions: {
      allergic_rate: 0.01,
      trali_rate: 0.001,
      taco_rate: 0.02
    }
  },
  
  logistics: {
    units_per_freezer_box: 10,
    thaw_capacity_per_hour: 4,
    resupply_frequency_days: 7,
    wastage_rate_percent: 15,
    
    thawer_types: {
      FIELD_THAWER: { capacity: 4, power_watts: 500 },
      HOSPITAL_THAWER: { capacity: 12, power_watts: 1500 }
    }
  },
  
  notes: 'Requires freezer storage. Thaw before use (30 min).'
};

/**
 * Freeze-Dried Plasma (FDP) - NEW in v2.0
 * 
 * French FLYP or German LyoPlas
 * Game-changer for forward resuscitation
 */
export const FREEZE_DRIED_PLASMA = {
  code: 'FDP',
  name: 'Freeze-Dried Plasma',
  abbreviation: 'FDP',
  nato_code: 'B2',
  alternative_names: ['FLYP', 'LyoPlas', 'Dried Plasma'],
  
  volume_ml: 200,  // After reconstitution
  weight_kg: 0.05, // Much lighter than FFP!
  
  storage: {
    temperature_c: { min: 2, max: 25 },  // Room temperature!
    shelf_life_years: 2,
    cold_chain_required: false,  // KEY ADVANTAGE
    transport_time_limit_hours: null,  // No limit
    
    reconstitution: {
      diluent: 'Sterile water',
      diluent_volume_ml: 200,
      time_minutes: 3,
      shake_required: true
    }
  },
  
  clinical: {
    fibrinogen_mg_per_unit: 280,  // Slightly less than FFP
    factor_activity_percent: 60,
    
    indications: [
      'Forward resuscitation',
      'Austere environments',
      'When FFP unavailable',
      'Point-of-injury plasma'
    ],
    
    transfusion_time_minutes: 15,
    crossmatch_required: false,
    
    reactions: {
      allergic_rate: 0.005,
      citrate_toxicity: 'LOWER'  // Less citrate than FFP
    }
  },
  
  logistics: {
    units_per_box: 20,  // Much more compact
    resupply_frequency_days: 30,
    wastage_rate_percent: 2,  // Very low due to long shelf life
    
    advantages: [
      'No cold chain required',
      'Long shelf life (2 years)',
      'Lightweight for transport',
      'Rapid reconstitution (3 min)',
      'Can be carried by medics'
    ],
    
    availability: {
      US: 'Limited (not FDA approved)',
      FRANCE: 'Standard issue',
      GERMANY: 'Standard issue',
      UK: 'Available',
      ROMANIA: 'Not yet available'
    }
  },
  
  ukraine_data: {
    usage: 'French FDP widely used',
    effectiveness: 'Equivalent to FFP for coagulopathy',
    preference: 'Preferred for forward units due to logistics'
  },
  
  notes: 'GAME CHANGER for forward resuscitation. No cold chain. 2-year shelf life.'
};

/**
 * Platelets
 */
export const PLATELETS = {
  code: 'PLT',
  name: 'Platelets',
  abbreviation: 'Plts',
  nato_code: 'C',
  
  volume_ml: 300,
  weight_kg: 0.35,
  
  storage: {
    temperature_c: { min: 20, max: 24 },
    shelf_life_days: 5,  // Very short!
    cold_chain_required: false,
    agitation_required: true,
    transport_time_limit_hours: 4
  },
  
  clinical: {
    platelet_count_per_unit: 3e11,
    expected_count_increase: 30000,
    transfusion_trigger: 50000,
    
    indications: [
      'Thrombocytopenia with bleeding',
      'Massive transfusion (1:1:1)',
      'Surgical hemostasis',
      'Platelet dysfunction'
    ],
    
    transfusion_time_minutes: 15,
    crossmatch_required: false,
    abo_preferred: true
  },
  
  logistics: {
    availability_forward: 'LIMITED',
    resupply_frequency_days: 2,
    wastage_rate_percent: 25,
    
    alternatives: {
      FWB: 'Fresh Whole Blood - contains functional platelets',
      COLD_STORED_PLATELETS: 'Experimental, better hemostasis',
      PLATELET_LYOPHILIZATE: 'Freeze-dried, experimental'
    }
  },
  
  notes: 'SHORT SHELF LIFE (5 days). Often unavailable forward.'
};

/**
 * Fresh Whole Blood (FWB)
 */
export const FRESH_WHOLE_BLOOD = {
  code: 'FWB',
  name: 'Fresh Whole Blood',
  abbreviation: 'FWB',
  nato_code: 'D',
  
  volume_ml: 450,
  weight_kg: 0.50,
  
  storage: {
    temperature_c: { min: 1, max: 6 },
    shelf_life_hours: 24,
    cold_chain_required: true,
    transport_time_limit_hours: 8,
    warm_ischemia_limit_hours: 8
  },
  
  clinical: {
    contains_all_components: true,
    hemoglobin_grams_per_unit: 60,
    platelet_function: 'PRESERVED',
    clotting_factors: 'FULL',
    
    indications: [
      'Massive hemorrhage',
      'Trauma resuscitation',
      'When components unavailable',
      'Field expedient'
    ],
    
    transfusion_time_minutes: 30,
    crossmatch_required: true,
    low_titer_o_plus_allowed: true,
    
    // Ukraine 2022-2024 validated superiority
    ukraine_data: {
      survival_improvement: 0.15,  // 15% better than component therapy
      usage_rate: 0.35,            // 35% of transfusions
      preference: 'STRONGLY_PREFERRED',
      notes: 'FWB showed superior outcomes vs 1:1:1 components'
    }
  },
  
  walking_blood_bank: {
    donor_screening_required: true,
    minimum_donors_registered: 50,
    donation_interval_days: 56,
    collection_time_minutes: 15,
    testing_time_minutes: 10,
    total_time_minutes: 25
  },
  
  notes: 'GOLD STANDARD for trauma. Ukraine 2022-2024: Superior outcomes.'
};

/**
 * Low-Titer O+ Whole Blood (LTOWB)
 */
export const LTOWB = {
  code: 'LTOWB',
  name: 'Low-Titer O-Positive Whole Blood',
  abbreviation: 'LTOWB',
  nato_code: 'D2',
  
  description: 'O+ whole blood with low anti-A/anti-B titers, safe for all recipients',
  
  volume_ml: 450,
  weight_kg: 0.50,
  
  titer_requirements: {
    anti_a_max: 256,
    anti_b_max: 256,
    testing_method: 'Tube or gel method',
    screening_frequency: 'Each donation'
  },
  
  clinical: {
    can_give_to: 'ALL_ABO_TYPES',
    hemolysis_risk: 'MINIMAL',
    preferred_for_emergencies: true,
    
    advantages: [
      'Universal donor for whole blood',
      'No crossmatch needed in emergency',
      'Contains all components',
      'Better than O-negative (more available)'
    ]
  },
  
  logistics: {
    o_positive_population: 0.37,
    low_titer_percentage: 0.60,  // 60% of O+ are low-titer
    effective_donor_pool: 0.22   // 22% of population
  },
  
  notes: 'Preferred emergency whole blood. Screen O+ donors for low titers.'
};

/**
 * Cryoprecipitate
 */
export const CRYOPRECIPITATE = {
  code: 'CRYO',
  name: 'Cryoprecipitate',
  abbreviation: 'Cryo',
  nato_code: 'E',
  
  volume_ml: 15,
  weight_kg: 0.02,
  
  storage: {
    temperature_c: { min: -18, max: -25 },
    shelf_life_days: 365,
    shelf_life_thawed_hours: 6,
    cold_chain_required: true,
    thaw_time_minutes: 15
  },
  
  clinical: {
    fibrinogen_mg_per_unit: 250,
    factor_viii_iu_per_unit: 100,
    von_willebrand_factor: 'PRESENT',
    factor_xiii: 'PRESENT',
    
    indications: [
      'Hypofibrinogenemia',
      'Massive transfusion',
      'DIC',
      'Von Willebrand disease'
    ],
    
    transfusion_time_minutes: 15,
    crossmatch_required: false,
    typical_dose_units: 10  // Pool of 10 units
  },
  
  logistics: {
    availability_forward: 'RARE',
    resupply_frequency_days: 14,
    wastage_rate_percent: 20
  },
  
  alternatives: {
    TXA: 'Tranexamic acid for anti-fibrinolysis',
    FIBRINOGEN_CONCENTRATE: 'RiaSTAP - more concentrated',
    FFP: 'Contains fibrinogen but lower concentration'
  },
  
  notes: 'High fibrinogen concentration. Often unavailable forward.'
};

// ============================================================================
// BLOOD PRODUCT AGGREGATION
// ============================================================================

export const BLOOD_PRODUCTS = {
  PRBC: PRBC,
  FFP: FFP,
  FDP: FREEZE_DRIED_PLASMA,
  PLT: PLATELETS,
  FWB: FRESH_WHOLE_BLOOD,
  LTOWB: LTOWB,
  CRYO: CRYOPRECIPITATE
};

// ============================================================================
// DAMAGE CONTROL RESUSCITATION (DCR)
// ============================================================================

/**
 * DCR is the overarching philosophy for trauma resuscitation
 * Includes permissive hypotension, hemostatic resuscitation, DCS
 */
export const DAMAGE_CONTROL_RESUSCITATION = {
  name: 'Damage Control Resuscitation',
  abbreviation: 'DCR',
  
  // Core principles
  principles: [
    'Permissive hypotension (SBP 80-90)',
    'Minimize crystalloids',
    'Early blood products (1:1:1)',
    'Whole blood preferred',
    'Prevent lethal triad',
    'Early TXA',
    'Damage control surgery'
  ],
  
  // Lethal triad
  lethal_triad: {
    hypothermia: {
      threshold_c: 35,
      coagulopathy_multiplier: 1.08  // Per degree below 35°C
    },
    acidosis: {
      threshold_ph: 7.2,
      base_deficit_threshold: -6
    },
    coagulopathy: {
      inr_threshold: 1.5,
      fibrinogen_threshold_gdl: 1.5
    }
  },
  
  // Permissive hypotension targets
  blood_pressure_targets: {
    penetrating_trauma: {
      sbp_target: 80,
      map_target: 50,
      rationale: 'Minimize clot disruption'
    },
    blunt_trauma: {
      sbp_target: 90,
      map_target: 65,
      rationale: 'Balance perfusion vs hemostasis'
    },
    tbi_present: {
      sbp_target: 110,
      map_target: 80,
      rationale: 'Maintain cerebral perfusion'
    }
  },
  
  // Fluid strategy
  fluid_strategy: {
    crystalloid_max_ml: 1000,  // Limit crystalloids
    preferred_resuscitation: 'BLOOD_PRODUCTS',
    
    order_of_preference: [
      'Fresh Whole Blood (LTOWB)',
      '1:1:1 Component therapy',
      'PRBC + FFP (if no platelets)',
      'PRBC + FDP (forward areas)',
      'PRBC alone (last resort)'
    ]
  },
  
  // Ukraine 2022-2024 lessons
  ukraine_lessons: {
    fwb_superiority: 'FWB showed 15% survival improvement',
    txa_critical: 'TXA within 1 hour: 32% mortality reduction',
    crystalloid_harm: 'Excessive crystalloid associated with worse outcomes',
    early_products: 'Earlier blood = better outcomes',
    cold_chain_challenge: 'FDP valuable due to cold chain issues'
  }
};

// ============================================================================
// MASSIVE TRANSFUSION PROTOCOL (MTP)
// ============================================================================

export const MASSIVE_TRANSFUSION_PROTOCOL = {
  name: 'NATO 1:1:1 Protocol',

  // CALC-014 (2026-10-09): moved here from literals in supplyIntelligence (values unchanged; the two
  // figures differ and should be confirmed against the source).
  minimum_stock: { prbc: 40, ffp: 40 },   // MTP readiness requirement (units)
  minimum_stock_warning: { prbc: 20 },    // stock below this raises a trauma-capability warning (units)

  // Trigger criteria
  triggers: {
    blood_loss_ml: 1500,
    transfusion_units: 4,
    systolic_bp_mmhg: 90,
    heart_rate_bpm: 120,
    base_deficit: -6,
    inr: 1.5,
    shock_index: 1.0,
    
    // Assessment scores
    abc_score: {
      penetrating_mechanism: 1,
      sbp_90_or_less: 1,
      hr_120_or_more: 1,
      positive_fast: 1,
      threshold: 2  // ≥2 triggers MTP
    }
  },
  
  // MTP packs
  packs: {
    PACK_1: {
      prbc_units: 6,
      ffp_units: 6,
      platelet_units: 6,
      cryo_units: 0
    },
    PACK_2: {
      prbc_units: 6,
      ffp_units: 6,
      platelet_units: 6,
      cryo_units: 10
    },
    FWB_ALTERNATIVE: {
      fwb_units: 4,
      notes: '4 FWB ≈ 1 MTP pack'
    }
  },
  
  ratios: {
    prbc_to_ffp: '1:1',
    prbc_to_platelets: '1:1',
    recommended: '1:1:1 (PRBC:FFP:PLT)',
    fwb_preferred: true
  },
  
  adjuncts: {
    txa: {
      name: 'Tranexamic Acid (TXA)',
      dose_mg: 1000,
      timing: 'Within 3 hours of injury',
      mortality_reduction_percent: 32,
      required: true
    },
    calcium: {
      name: 'Calcium Gluconate/Chloride',
      dose_mg: 1000,
      timing: 'After every 4 units PRBC',
      reason: 'Citrate toxicity prevention',
      required: true
    },
    fibrinogen: {
      name: 'Fibrinogen Concentrate',
      dose_g: 4,
      trigger_level_gdl: 1.5,
      required: false,
      alternative: 'Cryoprecipitate 10 units'
    }
  },
  
  monitoring: {
    lab_frequency_minutes: 30,
    point_of_care_testing: true,
    parameters: [
      'Hemoglobin',
      'Platelet count',
      'PT/INR',
      'Fibrinogen',
      'Lactate',
      'Base deficit',
      'Calcium (ionized)',
      'Temperature',
      'pH'
    ],
    
    // TEG/ROTEM if available
    viscoelastic_testing: {
      recommended: true,
      devices: ['TEG', 'ROTEM'],
      guides_therapy: true
    }
  },
  
  termination_criteria: [
    'Bleeding controlled surgically',
    'Hemodynamically stable',
    'Labs normalized',
    'No ongoing resuscitation needs'
  ]
};

// ============================================================================
// BLOOD CONSUMPTION RATES
// ============================================================================

export const BLOOD_CONSUMPTION_RATES = {
  BY_TRIAGE: {
    T1_IMMEDIATE: {
      prbc_units: 8,
      ffp_units: 8,
      platelet_units: 6,
      cryo_units: 10,
      fwb_equivalent: 6,
      mtp_probability: 0.60,
      notes: 'Surgical emergencies, massive hemorrhage'
    },
    T2_DELAYED: {
      prbc_units: 4,
      ffp_units: 2,
      platelet_units: 0,
      cryo_units: 0,
      fwb_equivalent: 2,
      mtp_probability: 0.15,
      notes: 'Moderate injuries, some blood loss'
    },
    T3_MINIMAL: {
      prbc_units: 0,
      ffp_units: 0,
      platelet_units: 0,
      cryo_units: 0,
      fwb_equivalent: 0,
      mtp_probability: 0,
      notes: 'Minor injuries, no transfusion expected'
    },
    T4_EXPECTANT: {
      prbc_units: 2,
      ffp_units: 0,
      platelet_units: 0,
      cryo_units: 0,
      fwb_equivalent: 1,
      mtp_probability: 0.10,
      notes: 'Comfort care, limited resources'
    }
  },
  
  BY_MECHANISM: {
    ARTILLERY: {
      prbc_units: 8,
      ffp_units: 6,
      mtp_probability: 0.45,
      notes: 'Multiple fragment wounds'
    },
    DRONE: {
      prbc_units: 6,
      ffp_units: 5,
      mtp_probability: 0.40,
      notes: 'Head/neck wounds, high mortality'
    },
    SMALL_ARMS: {
      prbc_units: 5,
      ffp_units: 4,
      mtp_probability: 0.30,
      notes: 'Single penetrating wounds'
    },
    MINE_IED: {
      prbc_units: 12,
      ffp_units: 10,
      mtp_probability: 0.65,
      notes: 'Traumatic amputation, massive hemorrhage'
    },
    VEHICLE_FIRE: {
      prbc_units: 10,
      ffp_units: 8,
      mtp_probability: 0.55,
      notes: 'Burns + trauma, high plasma needs'
    }
  },
  
  BY_OPERATION: {
    DAMAGE_CONTROL_LAPAROTOMY: {
      prbc_units: 12,
      ffp_units: 10,
      platelet_units: 6,
      duration_minutes: 45
    },
    THORACOTOMY: {
      prbc_units: 15,
      ffp_units: 12,
      platelet_units: 6,
      duration_minutes: 60
    },
    VASCULAR_REPAIR: {
      prbc_units: 8,
      ffp_units: 6,
      platelet_units: 4,
      duration_minutes: 90
    },
    ORTHOPEDIC_MAJOR: {
      prbc_units: 4,
      ffp_units: 2,
      platelet_units: 0,
      duration_minutes: 120
    },
    CRANIOTOMY: {
      prbc_units: 4,
      ffp_units: 4,
      platelet_units: 4,
      duration_minutes: 180
    },
    AMPUTATION: {
      prbc_units: 6,
      ffp_units: 4,
      platelet_units: 2,
      duration_minutes: 60
    }
  }
};

// ============================================================================
// STOCKAGE OBJECTIVES
// ============================================================================

export const BLOOD_STOCKAGE_OBJECTIVES = {
  ROLE_1: {
    name: 'Battalion Aid Station',
    echelon: 'BATTALION',
    prbc_units: 4,
    ffp_units: 0,
    fdp_units: 4,  // FDP now carried forward
    platelet_units: 0,
    fwb_donors: 20,
    dos_target: 0.5,
    notes: 'Emergency O-neg + FDP. Rely on WBB.'
  },
  
  ROLE_2_BASIC: {
    name: 'Brigade Medical Company',
    echelon: 'BRIGADE',
    prbc_units: 50,
    ffp_units: 30,
    fdp_units: 20,
    platelet_units: 0,
    cryo_units: 20,
    fwb_donors: 50,
    dos_target: 3,
    notes: '3 DOS. No platelets - use FWB.'
  },
  
  ROLE_2_ENHANCED: {
    name: 'Division Forward Surgical',
    echelon: 'DIVISION',
    prbc_units: 100,
    ffp_units: 80,
    fdp_units: 40,
    platelet_units: 20,
    cryo_units: 40,
    fwb_donors: 100,
    dos_target: 4,
    notes: '4 DOS. Limited platelets.'
  },
  
  ROLE_3: {
    name: 'Combat Support Hospital',
    echelon: 'CORPS',
    prbc_units: 300,
    ffp_units: 250,
    fdp_units: 50,
    platelet_units: 50,
    cryo_units: 100,
    fwb_donors: 200,
    dos_target: 7,
    notes: '7 DOS. Full blood bank capability.'
  },
  
  BLOOD_SUPPORT_DETACHMENT: {
    name: 'Blood Support Detachment',
    echelon: 'THEATER',
    prbc_units: 500,
    ffp_units: 400,
    fdp_units: 200,
    platelet_units: 100,
    cryo_units: 200,
    fwb_donors: 0,
    dos_target: 14,
    notes: 'Theater reserve. Resupplies Role 2/3.'
  }
};

// ============================================================================
// WALKING BLOOD BANK (WBB)
// ============================================================================

export const WALKING_BLOOD_BANK = {
  name: 'Walking Blood Bank Program',
  abbreviation: 'WBB',
  
  donor_requirements: {
    age_min: 17,
    age_max: 65,
    weight_min_kg: 50,
    hemoglobin_min_gdl: 12.5,
    donation_interval_days: 56,
    
    screening_tests: [
      'ABO/Rh typing',
      'Antibody screen',
      'HIV 1/2',
      'Hepatitis B (HBsAg)',
      'Hepatitis C',
      'Syphilis (RPR)',
      'HTLV I/II'
    ],
    
    rapid_tests_available: {
      hiv: true,
      hbv: true,
      hcv: true,
      syphilis: true,
      time_minutes: 15
    }
  },
  
  low_titer_o_plus: {
    name: 'Low-Titer O+ (LTOWB)',
    titer_threshold: 256,
    can_give_to: 'ALL_ABO_TYPES',
    preferred_for_emergencies: true,
    screening_additional: 'IgM Anti-A/B titers',
    o_plus_low_titer_rate: 0.60  // 60% of O+ are low-titer
  },
  
  collection: {
    registration_time_minutes: 5,
    screening_time_minutes: 10,
    collection_time_minutes: 10,
    rest_time_minutes: 15,
    total_time_minutes: 40,
    
    volume_collected_ml: 450,
    anticoagulant: 'CPDA-1',
    bag_type: 'Single collection bag',
    
    can_perform_at: ['ROLE_1', 'ROLE_2', 'ROLE_3'],
    
    equipment_required: [
      'Collection bags with CPDA-1',
      'Phlebotomy supplies',
      'Rapid test kits',
      'Cooler with ice',
      'Donor forms',
      'Blood type cards'
    ]
  },
  
  activation: {
    trigger: 'Blood shortage OR MTP activation',
    notification_method: 'Unit recall',
    response_time_minutes: 30,
    donors_per_activation: 10,
    
    activation_levels: {
      LEVEL_1: { donors: 5, description: 'Minor shortage' },
      LEVEL_2: { donors: 10, description: 'Moderate shortage' },
      LEVEL_3: { donors: 20, description: 'Critical shortage / MTP' }
    }
  },
  
  planning: {
    donors_per_1000_troops: 50,
    availability_rate: 0.70,
    successful_collection_rate: 0.95,
    units_per_activation: 7,
    // CALC-014 (2026-10-09): moved here from literals in casualtyCalculator and supplyIntelligence
    // (values unchanged; sources still to be confirmed). Note donors_per_1000_troops (5%) and
    // eligible_percent (2.5%) describe related shares and should be reconciled.
    eligibility_rate: 0.65,   // share of donors typically eligible on the day
    emergency_yield: 0.80,    // share of eligible donors who actually donate in an emergency
    eligible_percent: 0.025   // share of trained personnel counted as eligible in supply recommendations
  },
  
  ukraine_data: {
    wbb_usage: 'Extensive',
    fwb_percentage: 0.35,
    outcomes: 'Superior to component therapy',
    lessons: 'Pre-registration essential, training critical'
  }
};

// ============================================================================
// BLOOD ALERT LEVELS
// ============================================================================

export const BLOOD_ALERT_LEVELS = {
  GREEN: {
    level: 'GREEN',
    label: 'Adequate',
    description: 'Blood supply adequate for current operations',
    dos_range: { min: 5, max: null },
    actions: ['Routine resupply'],
    color: '#4caf50',
    dow_modifier: 1.00,
    additional_dow_percent: 0,
    shortage_impact: 'NONE',
    wbb_activation_recommended: false,
    conservation_protocols: false,
    crisis_standards: false,
    expectant_triage_increase: false
  },
  YELLOW: {
    level: 'YELLOW',
    label: 'Caution',
    description: 'Blood supply below optimal, monitor closely',
    dos_range: { min: 3, max: 5 },
    actions: [
      'Expedite resupply',
      'Alert WBB coordinators',
      'Review consumption'
    ],
    color: '#ff9800',
    dow_modifier: 1.15,
    additional_dow_percent: 15,
    shortage_impact: 'MODERATE',
    wbb_activation_recommended: true,
    conservation_protocols: false,
    crisis_standards: false,
    expectant_triage_increase: false
  },
  RED: {
    level: 'RED',
    label: 'Critical',
    description: 'Blood supply critical, restrict non-emergency use',
    dos_range: { min: 1, max: 3 },
    actions: [
      'Emergency resupply',
      'Activate WBB Level 2',
      'Cancel elective procedures',
      'Notify higher headquarters'
    ],
    color: '#f44336',
    dow_modifier: 1.35,
    additional_dow_percent: 35,
    shortage_impact: 'SEVERE',
    wbb_activation_recommended: true,
    wbb_activation_required: true,
    conservation_protocols: true,
    crisis_standards: false,
    expectant_triage_increase: false
  },
  BLACK: {
    level: 'BLACK',
    label: 'Emergency',
    description: 'Blood supply exhausted or near-exhausted',
    dos_range: { min: 0, max: 1 },
    actions: [
      'Activate WBB Level 3',
      'Request emergency resupply',
      'Implement crisis standards',
      'Request theater support'
    ],
    color: '#000000',
    dow_modifier: 1.60,
    additional_dow_percent: 60,
    shortage_impact: 'CRITICAL',
    wbb_activation_recommended: true,
    wbb_activation_required: true,
    conservation_protocols: true,
    crisis_standards: true,
    expectant_triage_increase: true
  }
};

// ============================================================================
// COAGULOPATHY ASSESSMENT
// ============================================================================

export const COAGULOPATHY_ASSESSMENT = {
  // Risk factors for trauma-induced coagulopathy
  risk_factors: {
    shock: { hr_sbp_ratio: 1.0, points: 2 },
    hypothermia: { temp_c: 35, points: 2 },
    acidosis: { ph: 7.2, points: 2 },
    hemodilution: { crystalloid_l: 2, points: 1 },
    tissue_injury: { iss_score: 25, points: 1 },
    time_to_treatment: { hours: 2, points: 1 }
  },
  
  // Laboratory thresholds
  lab_thresholds: {
    inr_normal: 1.0,
    inr_abnormal: 1.5,
    inr_critical: 2.0,
    fibrinogen_normal_gdl: 2.5,
    fibrinogen_low_gdl: 1.5,
    fibrinogen_critical_gdl: 1.0,
    platelet_normal: 150000,
    platelet_low: 100000,
    platelet_critical: 50000
  },
  
  // Treatment targets
  treatment_targets: {
    inr_target: 1.5,
    fibrinogen_target_gdl: 2.0,
    platelet_target: 100000,
    temperature_target_c: 36,
    ph_target: 7.35,
    calcium_target_mmol: 1.1
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get blood product specification by code
 */
export const getBloodProduct = (code) => {
  return BLOOD_PRODUCTS[code] || null;
};

/**
 * Check if blood types are compatible
 */
export const isBloodTypeCompatible = (recipientType, donorType, productType = 'RBC') => {
  const matrix = productType === 'PLASMA' 
    ? BLOOD_TYPE_COMPATIBILITY.PLASMA_COMPATIBILITY
    : BLOOD_TYPE_COMPATIBILITY.RBC_COMPATIBILITY;
  
  const compatibleDonors = matrix[recipientType];
  return compatibleDonors ? compatibleDonors.includes(donorType) : false;
};

/**
 * Get compatible donors for a recipient
 */
export const getCompatibleDonors = (recipientType, productType = 'RBC') => {
  const matrix = productType === 'PLASMA'
    ? BLOOD_TYPE_COMPATIBILITY.PLASMA_COMPATIBILITY
    : BLOOD_TYPE_COMPATIBILITY.RBC_COMPATIBILITY;
  
  return matrix[recipientType] || [];
};

/**
 * Calculate blood requirements for casualties
 */
export const calculateBloodRequirements = (casualties) => {
  const requirements = {
    prbc: 0,
    ffp: 0,
    fdp: 0,
    platelets: 0,
    cryo: 0,
    fwb_equivalent: 0,
    mtp_packs: 0
  };
  
  const rates = BLOOD_CONSUMPTION_RATES.BY_TRIAGE;
  
  if (casualties.t1 > 0) {
    const rate = rates.T1_IMMEDIATE;
    requirements.prbc += casualties.t1 * rate.prbc_units;
    requirements.ffp += casualties.t1 * rate.ffp_units;
    requirements.platelets += casualties.t1 * rate.platelet_units;
    requirements.cryo += casualties.t1 * rate.cryo_units;
    requirements.fwb_equivalent += casualties.t1 * rate.fwb_equivalent;
    requirements.mtp_packs += Math.ceil(casualties.t1 * rate.mtp_probability);
  }
  
  if (casualties.t2 > 0) {
    const rate = rates.T2_DELAYED;
    requirements.prbc += casualties.t2 * rate.prbc_units;
    requirements.ffp += casualties.t2 * rate.ffp_units;
    requirements.fwb_equivalent += casualties.t2 * rate.fwb_equivalent;
  }
  
  // FDP can substitute for FFP in forward areas
  requirements.fdp = Math.ceil(requirements.ffp * 0.5);
  
  return requirements;
};

/**
 * Calculate blood requirements by mechanism
 */
export const calculateBloodByMechanism = (mechanismCounts) => {
  const requirements = { prbc: 0, ffp: 0, mtp_count: 0 };
  const rates = BLOOD_CONSUMPTION_RATES.BY_MECHANISM;
  
  for (const [mechanism, count] of Object.entries(mechanismCounts)) {
    const rate = rates[mechanism];
    if (rate && count > 0) {
      requirements.prbc += count * rate.prbc_units;
      requirements.ffp += count * rate.ffp_units;
      requirements.mtp_count += Math.ceil(count * rate.mtp_probability);
    }
  }
  
  return requirements;
};

/**
 * Calculate Days of Supply
 */
export const calculateDaysOfSupply = (currentStock, dailyConsumption) => {
  if (dailyConsumption === 0) return 999;
  return Math.round((currentStock / dailyConsumption) * 10) / 10;
};

/**
 * Get blood alert level based on DOS
 */
export const getBloodAlertLevel = (daysOfSupply) => {
  if (daysOfSupply >= 5) return BLOOD_ALERT_LEVELS.GREEN;
  if (daysOfSupply >= 3) return BLOOD_ALERT_LEVELS.YELLOW;
  if (daysOfSupply >= 1) return BLOOD_ALERT_LEVELS.RED;
  return BLOOD_ALERT_LEVELS.BLACK;
};

/**
 * Get DOW modifier for blood shortage
 */
export const getBloodShortageDOWModifier = (alertLevel) => {
  const level = BLOOD_ALERT_LEVELS[alertLevel];
  if (!level) {
    return {
      modifier: 1.0,
      alert_level: 'UNKNOWN',
      shortage_impact: 'NONE',
      additional_dow_percent: 0,
      recommendations: []
    };
  }
  
  return {
    modifier: level.dow_modifier || 1.0,
    alert_level: level.level,
    shortage_impact: level.shortage_impact,
    additional_dow_percent: level.additional_dow_percent,
    crisis_standards: level.crisis_standards || false,
    expectant_triage_increase: level.expectant_triage_increase || false,
    recommendations: level.actions || []
  };
};

/**
 * Calculate WBB donor requirement
 */
export const calculateWBBDonorRequirement = (totalStrength) => {
  const wbb = WALKING_BLOOD_BANK.planning;
  const targetDonors = Math.ceil(totalStrength * wbb.donors_per_1000_troops / 1000);
  const expectedAvailable = Math.floor(targetDonors * wbb.availability_rate);
  const expectedUnits = Math.floor(expectedAvailable * wbb.successful_collection_rate);
  
  // LTOWB donors
  const oPlusPercent = BLOOD_TYPE_COMPATIBILITY.POPULATION_DISTRIBUTION['O+'];
  const ltowbDonors = Math.floor(targetDonors * oPlusPercent * WALKING_BLOOD_BANK.low_titer_o_plus.o_plus_low_titer_rate);
  
  return {
    total_strength: totalStrength,
    target_registered_donors: targetDonors,
    expected_available: expectedAvailable,
    expected_units_per_activation: expectedUnits,
    ltowb_donors: ltowbDonors
  };
};

/**
 * Check if MTP should be activated
 */
export const shouldActivateMTP = (patient) => {
  const triggers = MASSIVE_TRANSFUSION_PROTOCOL.triggers;
  let abcScore = 0;
  
  if (patient.mechanism === 'PENETRATING') abcScore++;
  if (patient.sbp <= triggers.systolic_bp_mmhg) abcScore++;
  if (patient.hr >= triggers.heart_rate_bpm) abcScore++;
  if (patient.fast_positive) abcScore++;
  
  return {
    activate: abcScore >= triggers.abc_score.threshold,
    abc_score: abcScore,
    threshold: triggers.abc_score.threshold,
    factors: {
      penetrating: patient.mechanism === 'PENETRATING',
      hypotension: patient.sbp <= triggers.systolic_bp_mmhg,
      tachycardia: patient.hr >= triggers.heart_rate_bpm,
      fast_positive: patient.fast_positive
    }
  };
};

/**
 * Calculate coagulopathy risk
 */
export const calculateCoagulopathyRisk = (patient) => {
  const factors = COAGULOPATHY_ASSESSMENT.risk_factors;
  let score = 0;
  const present = [];
  
  if (patient.shock_index >= factors.shock.hr_sbp_ratio) {
    score += factors.shock.points;
    present.push('SHOCK');
  }
  if (patient.temp_c < factors.hypothermia.temp_c) {
    score += factors.hypothermia.points;
    present.push('HYPOTHERMIA');
  }
  if (patient.ph < factors.acidosis.ph) {
    score += factors.acidosis.points;
    present.push('ACIDOSIS');
  }
  if (patient.crystalloid_l >= factors.hemodilution.crystalloid_l) {
    score += factors.hemodilution.points;
    present.push('HEMODILUTION');
  }
  
  const risk = score >= 4 ? 'HIGH' : score >= 2 ? 'MODERATE' : 'LOW';
  
  return {
    score: score,
    risk_level: risk,
    factors_present: present,
    lethal_triad: present.length >= 2 && present.includes('HYPOTHERMIA')
  };
};

/**
 * Get stockage objectives for echelon
 */
export const getStockageObjective = (echelon) => {
  const echelonMap = {
    'BATTALION': 'ROLE_1',
    'BRIGADE': 'ROLE_2_BASIC',
    'DIVISION': 'ROLE_2_ENHANCED',
    'CORPS': 'ROLE_3'
  };
  
  const key = echelonMap[echelon] || echelon;
  return BLOOD_STOCKAGE_OBJECTIVES[key] || null;
};

/**
 * Get blood product options for dropdown
 */
export const getBloodProductOptions = () => {
  return Object.entries(BLOOD_PRODUCTS).map(([code, product]) => ({
    value: code,
    label: product.name,
    abbreviation: product.abbreviation,
    cold_chain: product.storage.cold_chain_required,
    shelf_life_days: product.storage.shelf_life_days || 
                     (product.storage.shelf_life_hours ? product.storage.shelf_life_hours / 24 : 0) ||
                     (product.storage.shelf_life_years ? product.storage.shelf_life_years * 365 : 0)
  }));
};

/**
 * Validate blood product codes
 */
export const isValidBloodProduct = (code) => {
  return Object.keys(BLOOD_PRODUCTS).includes(code);
};

// ============================================================================
// GROUP-1-B: USCENTCOM "6 AND 2" BLOOD FLOOR RULE
// ============================================================================
// Source: "USCENTCOM 6 and 2 Blood Rule." Military Medicine, Vol. 189, Issue 9-10 (2024).
//         https://academic.oup.com/milmed/article/189/9-10/249/7645291
//
// USCENTCOM doctrinal minimum blood stock requirement for surgical operations:
//   Minimum LTOWB stock = (6 per OR bed) + (2 per POI team)
//
// OR bed = active operating table in a forward surgical element (Role 2B/2E).
//   - FST: 2 OR tables per team  (FORWARD_SURGICAL_TEAM.equipment.operating_tables)
//   - SURG_CO: 4 OR tables per team  (SURGICAL_COMPANY.capability.simultaneous_tables)
//
// POI team = Point of Injury team (Role 1 medic team at brigade/battalion level).
//   Approximated in PSP/CRE as: 1 POI team per brigade (conservative planning factor).
//
// LTOWB equivalence in PSP/CRE: LTOWB = 1 whole blood unit.
//   For planning when only component blood (PRBC/FFP) is stocked:
//   1 LTOWB ~= 1 PRBC + 1 FFP (1:1 component equivalent per TCCC/ATLS).
//   The check uses total blood units (PRBC + FFP + PLT + Cryo + WBB whole blood)
//   as a conservative proxy for available LTOWB-equivalent stock.
//
// Status thresholds (NATO traffic-light convention):
//   >= 100% of minimum  -> GREEN  (adequate)
//   >= 75% of minimum   -> AMBER  (marginal — plan resupply)
//   <  75% of minimum   -> RED    (critical — insufficient for doctrine)
// ============================================================================

/** LTOWB units required per active OR bed (operating table) */
export const BLOOD_FLOOR_PER_OR_BED = 6;

/** LTOWB units required per Point of Injury team (Role 1 / brigade-level) */
export const BLOOD_FLOOR_PER_POI_TEAM = 2;

/** OR beds per Forward Surgical Team (FST) */
export const BLOOD_FLOOR_FST_OR_BEDS = 2;

/** OR beds per Surgical Company (SURG_CO) */
export const BLOOD_FLOOR_SURGCO_OR_BEDS = 4;

/** Minimum stock fraction below which AMBER warning is triggered */
export const BLOOD_FLOOR_AMBER_THRESHOLD = 1.0;   // < 100% = amber

/** Minimum stock fraction below which RED critical alert is triggered */
export const BLOOD_FLOOR_RED_THRESHOLD = 0.75;    // < 75% = red

// ============================================================================
// EXPORTS
// ============================================================================

// Named default export (fixes lint warning)
const bloodProductsConstants = {
  // Blood type compatibility
  BLOOD_TYPE_COMPATIBILITY,
  
  // Individual products
  PRBC,
  FFP,
  FREEZE_DRIED_PLASMA,
  PLATELETS,
  FRESH_WHOLE_BLOOD,
  LTOWB,
  CRYOPRECIPITATE,
  
  // Aggregated
  BLOOD_PRODUCTS,
  DAMAGE_CONTROL_RESUSCITATION,
  MASSIVE_TRANSFUSION_PROTOCOL,
  BLOOD_CONSUMPTION_RATES,
  BLOOD_STOCKAGE_OBJECTIVES,
  WALKING_BLOOD_BANK,
  BLOOD_ALERT_LEVELS,
  COAGULOPATHY_ASSESSMENT,
  // GROUP-1-B: USCENTCOM 6-and-2 blood floor constants
  BLOOD_FLOOR_PER_OR_BED,
  BLOOD_FLOOR_PER_POI_TEAM,
  BLOOD_FLOOR_FST_OR_BEDS,
  BLOOD_FLOOR_SURGCO_OR_BEDS,
  BLOOD_FLOOR_AMBER_THRESHOLD,
  BLOOD_FLOOR_RED_THRESHOLD,
  
  // Helper functions
  getBloodProduct,
  isBloodTypeCompatible,
  getCompatibleDonors,
  calculateBloodRequirements,
  calculateBloodByMechanism,
  calculateDaysOfSupply,
  getBloodAlertLevel,
  getBloodShortageDOWModifier,
  calculateWBBDonorRequirement,
  shouldActivateMTP,
  calculateCoagulopathyRisk,
  getStockageObjective,
  getBloodProductOptions,
  isValidBloodProduct
};

export default bloodProductsConstants;
